import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  Divider,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#0c0d12', paper: '#14171f' },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232,233,238,0.7)',
    },
    primary: { main: '#6aa9ff' },
  },
  typography: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
});

const tableHeadCellSx = {
  fontWeight: 700,
  color: '#d9e6ff',
  backgroundColor: 'rgba(30, 35, 52, 0.92)',
  borderBottom: '1px solid rgba(255,255,255,0.12)',
  textAlign: 'center',
};

const DataTable = () => {
  const [cdrLogs, setCdrLogs] = useState([]);
  const [totals, setTotals] = useState({
    g1: { duration: 0, earnings: 0 },
    g2: { duration: 0, earnings: 0 },
    g3: { duration: 0, earnings: 0 },
    g4: { duration: 0, earnings: 0 },
    g5: { duration: 0, earnings: 0 },
    g6: { duration: 0, earnings: 0 },
    g7: { duration: 0, earnings: 0 },
    g8: { duration: 0, earnings: 0 },
    g9: { duration: 0, earnings: 0 },
  });

  const [selectedGateway, setSelectedGateway] = useState('All');
  const [selectedGateways, setSelectedGateways] = useState([]);
  const [selectedPort, setSelectedPort] = useState('All');

  const determineRate = (callee, sipUser) => {
    const specialPrefixes = [
      '0817', '0905', '0906', '0915', '0916', '0917', '0926', '0927', '0935',
      '0936', '0937', '0945', '0952', '0953', '0954', '0955', '0956', '0957',
      '0959', '0965', '0966', '0967', '0971', '0972', '0975', '0976', '0977',
      '0978', '0980', '0983', '0986', '0987', '0989', '0995', '0996', '0997',
    ];

    const sipUsers05Rate = ['1443R517-63CLI', '1443R518-63CLI', '1443R519-63CLI', '1443R517-63CC'];
    const sipUsersSpecialRate = [
      'Frek1-route-63', 'Frek2-route-63', 'Frek3-route-63', 'Frek4-route-63',
      'Frek5-route-63', 'Edz-route-63-1', 'Edz-route-63-2', 'Edz-route-63-3',
      'Edz-route-63-4', 'Edz-route-63-5',
    ];
    const sipUsersNewRate = ['Frek-route-63cc', 'Edz-route-63cc', 'Frek-route-a63cc', 'Edz-route-a63cc', '1443R518-63CC'];
    const sipUsersAdditionalRate = {
      '0014R648-63': 0.0006667,
      '0014R649-63': 0.0006667,
      '0014R651-63': 0.00033333333,
      '0112R0033NCLI63': 0.0006667,
      '0112R0033NCLI63-2': 0.0006667,
    };

    if (sipUsers05Rate.includes(sipUser)) return 0.0005833;

    if (sipUsersSpecialRate.some((prefix) => sipUser.startsWith(prefix))) {
      if (specialPrefixes.some((prefix) => callee.startsWith(prefix))) return 0.00031666666;
      return 0.00031666666;
    }

    if (sipUsersNewRate.includes(sipUser)) {
      if (specialPrefixes.some((prefix) => callee.startsWith(prefix))) return 0.0006667;
      return 0.0006667;
    }

    if (sipUser in sipUsersAdditionalRate) return sipUsersAdditionalRate[sipUser];

    return 0.00031666666;
  };

  const formatDuration = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [hours, minutes, seconds].map((v) => (v < 10 ? `0${v}` : v)).join(':');
  };

  useEffect(() => {
    const fetchCdrLogs = async () => {
      const gateways = ['g1', 'g2', 'g3', 'g4', 'g5', 'g6', 'g7', 'g8', 'g9'];
      let allLogs = [];
      let newTotals = {
        g1: { duration: 0, earnings: 0 },
        g2: { duration: 0, earnings: 0 },
        g3: { duration: 0, earnings: 0 },
        g4: { duration: 0, earnings: 0 },
        g5: { duration: 0, earnings: 0 },
        g6: { duration: 0, earnings: 0 },
        g7: { duration: 0, earnings: 0 },
        g8: { duration: 0, earnings: 0 },
        g9: { duration: 0, earnings: 0 },
      };

      for (const gateway of gateways) {
        try {
          const response = await axios.get(`https://frek.cc/proxy/38.54.86.24:5006/logs/cdr/${gateway}`);
          const gatewayLogs = response.data.map((log) => ({ ...log, Gateway: gateway }));
          allLogs = allLogs.concat(gatewayLogs);

          gatewayLogs.forEach((log) => {
            newTotals[gateway].duration += log.Duration;
            const rate = determineRate(log.Callee, log['sip user']);
            newTotals[gateway].earnings += log.Duration * rate;
          });
        } catch (error) {
          console.error(`Error fetching CDR logs from ${gateway}:`, error);
        }
      }

      allLogs.sort((a, b) => new Date(b.End) - new Date(a.End));
      setCdrLogs(allLogs);
      setTotals(newTotals);
    };

    fetchCdrLogs();
    const interval = setInterval(fetchCdrLogs, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.title = 'Call logs';
  }, []);

  const filteredLogs = cdrLogs.filter(
    (log) =>
      (selectedGateway === 'All' || log.Gateway === selectedGateway || selectedGateways.includes(log.Gateway)) &&
      (selectedPort === 'All' || Math.floor(log.Port) === parseInt(selectedPort, 10))
  );

  const totalConsumption = Object.values(totals).reduce((sum, { duration }) => sum + duration, 0);
  const totalEarnings = Object.values(totals).reduce((sum, { earnings }) => sum + earnings, 0);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
            'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
            'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <Stack spacing={1.2}>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                  VOIP Realtime Logs
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`Duration ${formatDuration(totalConsumption)}`} sx={{ bgcolor: 'rgba(106,169,255,0.2)', color: '#d5e7ff', fontWeight: 700 }} />
                  <Chip label={`Bill USD ${totalEarnings.toFixed(2)}`} sx={{ bgcolor: 'rgba(90,210,122,0.18)', color: '#b9f2c8', fontWeight: 700 }} />
                </Stack>
              </Stack>
            </Paper>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
              <Paper
                elevation={0}
                sx={{
                  width: { xs: '100%', lg: 420 },
                  borderRadius: 3,
                  background: 'rgba(18, 18, 22, 0.75)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>Hourly VOIP Data</Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Goip</TableCell>
                        <TableCell sx={tableHeadCellSx}>Consumption</TableCell>
                        <TableCell sx={tableHeadCellSx}>Bill</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(totals).map(([gateway, { duration, earnings }]) => (
                        <TableRow key={gateway} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell align="center">{gateway}</TableCell>
                          <TableCell align="center">{formatDuration(duration)}</TableCell>
                          <TableCell align="center">USD {earnings.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)', fontWeight: 700 } }}>
                        <TableCell align="center">Total</TableCell>
                        <TableCell align="center">{formatDuration(totalConsumption)}</TableCell>
                        <TableCell align="center">USD {totalEarnings.toFixed(2)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  borderRadius: 3,
                  background: 'rgba(18, 18, 22, 0.75)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>VOIP Realtime Logs</Typography>
                  <Stack direction="row" spacing={1}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={selectedGateway}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedGateway(value);
                          if (value === 'CCYX') setSelectedGateways(['g3', 'g4']);
                          else if (value === 'CLIYX') setSelectedGateways(['g1', 'g2']);
                          else if (value === 'SK') setSelectedGateways(['g5', 'g6']);
                          else setSelectedGateways([]);
                        }}
                        sx={{ height: 34, backgroundColor: 'rgba(255,255,255,0.03)' }}
                      >
                        <MenuItem value="All">All Gateways</MenuItem>
                        <MenuItem value="g1">Goip 1</MenuItem>
                        <MenuItem value="g2">Goip 2</MenuItem>
                        <MenuItem value="g3">Goip 3</MenuItem>
                        <MenuItem value="g4">Goip 4</MenuItem>
                        <MenuItem value="g5">Goip 5</MenuItem>
                        <MenuItem value="g6">Goip 6</MenuItem>
                        <MenuItem value="g7">Goip 7</MenuItem>
                        <MenuItem value="g8">Goip 8</MenuItem>
                        <MenuItem value="g9">Goip 9</MenuItem>
                        <MenuItem value="CCYX">YX CC Route</MenuItem>
                        <MenuItem value="CLIYX">YX CLI Route</MenuItem>
                        <MenuItem value="SK">SK Route</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={selectedPort}
                        onChange={(e) => setSelectedPort(e.target.value)}
                        sx={{ height: 34, backgroundColor: 'rgba(255,255,255,0.03)' }}
                      >
                        <MenuItem value="All">All Ports</MenuItem>
                        {[...Array(32).keys()].map((port) => (
                          <MenuItem key={port + 1} value={port + 1}>{`Port ${port + 1}`}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Box>
                <TableContainer sx={{ maxHeight: '76vh' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>Duration</TableCell>
                        <TableCell sx={tableHeadCellSx}>Stats</TableCell>
                        <TableCell sx={tableHeadCellSx}>Callee</TableCell>
                        <TableCell sx={tableHeadCellSx}>Goip</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLogs.slice(0, 10000).map((log, index) => {
                        const minutes = Math.floor(log.Duration / 60);
                        const seconds = log.Duration % 60;
                        const durationString = `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
                        if (durationString === '00:00') return null;

                        return (
                          <TableRow key={index} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                            <TableCell align="center" sx={{ color: durationString >= '00:30' ? '#5ad27a' : 'inherit' }}>{durationString}</TableCell>
                            <TableCell align="center">{log.Reason}</TableCell>
                            <TableCell align="center">{log.Callee.startsWith('0063') ? log.Callee.substring(2) : log.Callee}</TableCell>
                            <TableCell align="center">{log.Gateway}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DataTable;
